import type { TFunction } from 'i18next';
import type { StatusIconName } from '../../components/ui/StatusIcon';
import type { StopEndSource, StopPayer, StopReason } from '../../api/types';

/** Longest note and place the API accepts (dnm-work#165 C2). */
export const STOP_NOTE_MAX = 500;
export const STOP_PLACE_MAX = 255;

/** The eight reasons in the Contract's order, each with its shared SVG icon (R14). */
export const STOP_REASONS: readonly { code: StopReason; icon: StatusIconName }[] = [
  { code: 'DRIVER_PERSONAL', icon: 'schedule' },
  { code: 'BREAKDOWN', icon: 'local_shipping' },
  { code: 'ACCIDENT', icon: 'warning' },
  { code: 'POLICE_RTO', icon: 'event_busy' },
  { code: 'NO_ENTRY_HOURS', icon: 'hourglass' },
  { code: 'WAITING_FOR_PAPERS', icon: 'history' },
  { code: 'CUSTOMER_HOLD', icon: 'phone' },
  { code: 'OTHER', icon: 'edit' },
];

export const STOP_PAYERS: readonly StopPayer[] = ['DRIVER', 'TRUCK', 'ROAD', 'CUSTOMER'];

const REASON_KEYS = {
  DRIVER_PERSONAL: 'stops.reason.DRIVER_PERSONAL',
  BREAKDOWN: 'stops.reason.BREAKDOWN',
  ACCIDENT: 'stops.reason.ACCIDENT',
  POLICE_RTO: 'stops.reason.POLICE_RTO',
  NO_ENTRY_HOURS: 'stops.reason.NO_ENTRY_HOURS',
  WAITING_FOR_PAPERS: 'stops.reason.WAITING_FOR_PAPERS',
  CUSTOMER_HOLD: 'stops.reason.CUSTOMER_HOLD',
  OTHER: 'stops.reason.OTHER',
} as const satisfies Record<StopReason, string>;

const PAYER_KEYS = {
  DRIVER: 'stops.payer.DRIVER',
  TRUCK: 'stops.payer.TRUCK',
  ROAD: 'stops.payer.ROAD',
  CUSTOMER: 'stops.payer.CUSTOMER',
} as const satisfies Record<StopPayer, string>;

const END_SOURCE_KEYS = {
  MANUAL: 'stops.endSource.MANUAL',
  GPS: 'stops.endSource.GPS',
  MILESTONE: 'stops.endSource.MILESTONE',
  CANCELLED: 'stops.endSource.CANCELLED',
} as const satisfies Record<StopEndSource, string>;

const has = (o: object, k: string | null | undefined): boolean =>
  !!k && Object.prototype.hasOwnProperty.call(o, k);

/** Reason label; an unknown or newer code reads "Other reason", never the raw code. */
export function stopReasonLabel(t: TFunction, reason: string | null | undefined): string {
  return has(REASON_KEYS, reason)
    ? t(REASON_KEYS[reason as StopReason])
    : t('stops.reason.unknown');
}

/** Payer label; null (stops migrated from the old EXCEPTION status, rhino-api R56) reads "Not set". */
export function stopPayerLabel(t: TFunction, payer: string | null | undefined): string {
  return has(PAYER_KEYS, payer) ? t(PAYER_KEYS[payer as StopPayer]) : t('stops.payer.notSet');
}

/** How the stop ended, in words; null = still open. */
export function stopEndSourceLabel(t: TFunction, endSource: string | null | undefined): string {
  return has(END_SOURCE_KEYS, endSource)
    ? t(END_SOURCE_KEYS[endSource as StopEndSource])
    : t('stops.endSource.open');
}

/** Whole server minutes as "Xh Ym" (or "Ym" under an hour). Display only: the value is never recomputed (P3). */
export function formatStopMinutes(t: TFunction, minutes: number): string {
  const safe = Math.max(0, Math.floor(minutes));
  const hours = Math.floor(safe / 60);
  const rest = safe % 60;
  return hours > 0
    ? t('stops.duration.hoursMinutes', { hours, minutes: rest })
    : t('stops.duration.minutes', { minutes: rest });
}

export type StopProblem = 'NOTE_REQUIRED' | 'NOTE_TOO_LONG' | 'PLACE_TOO_LONG' | 'TIME_IN_PAST';

/** The checks the API makes (C2), run before the call so the message shows at once; the server stays the authority. */
export function stopProblem(
  input: {
    reason: StopReason | null;
    note: string;
    place: string;
    expectedResumeAt: string | null;
  },
  now: number = Date.now(),
): StopProblem | null {
  const note = input.note.trim();
  if (input.reason === 'OTHER' && note.length === 0) return 'NOTE_REQUIRED';
  if (note.length > STOP_NOTE_MAX) return 'NOTE_TOO_LONG';
  if (input.place.trim().length > STOP_PLACE_MAX) return 'PLACE_TOO_LONG';
  if (input.expectedResumeAt && Date.parse(input.expectedResumeAt) <= now) return 'TIME_IN_PAST';
  return null;
}
